// Back-view non-muscle structure layer for pain capture
// (PAIN_TRACKING_PLAN.md Task 2.2). React-free / JSX-free (hazard H7).
//
// Same 220x520 canvas as regionsBack.ts (the silhouette is shared with the
// front view). Screen-left half hand-authored; pairPainRegions mirrors it
// (axis x = 110). Spine segments run down the midline and are single
// centerPainRegions.
//
// Coverage (Task 2.2): shoulder / elbow / wrist / hip / knee / ankle joints;
// SI joint; spine-c / spine-t / spine-l1..l5 / spine-s segments; sciatic nerve
// track; L4 / L5 / S1 dermatome bands; calcaneus.

import type { PainRegion } from "./painRegions.js";
import { centerPainRegion, ellipsePath, pairPainRegions } from "./painRegions.js";

export const BACK_STRUCTURES: PainRegion[] = [
  // ── joints ─────────────────────────────────────────────────────────────────
  ...pairPainRegions("joint-shoulder", ellipsePath(56, 116, 9, 10)),
  ...pairPainRegions("joint-elbow", ellipsePath(48, 196, 9, 9)),
  ...pairPainRegions("joint-wrist", ellipsePath(45, 256, 8, 7)),
  // From behind, the hip joint reads at the buttock / greater-trochanter mass.
  ...pairPainRegions("joint-hip", ellipsePath(64, 262, 10, 11)),
  ...pairPainRegions("joint-knee", ellipsePath(86, 364, 11, 12)),
  ...pairPainRegions("joint-ankle", ellipsePath(86, 481, 8, 7)),
  // SI joint: the small area just lateral to the sacrum, beside the tailbone.
  ...pairPainRegions("joint-si", ellipsePath(101, 250, 6, 8)),

  // ── spine segments (midline column, occiput to sacrum) ────────────────────
  centerPainRegion("spine-c", ellipsePath(110, 92, 5, 12)),
  centerPainRegion("spine-t", ellipsePath(110, 150, 5, 40)),
  centerPainRegion("spine-l1", ellipsePath(110, 198, 5, 6)),
  centerPainRegion("spine-l2", ellipsePath(110, 212, 5, 6)),
  centerPainRegion("spine-l3", ellipsePath(110, 226, 5, 6)),
  centerPainRegion("spine-l4", ellipsePath(110, 240, 5, 6)),
  centerPainRegion("spine-l5", ellipsePath(110, 254, 5, 6)),
  centerPainRegion("spine-s", ellipsePath(110, 270, 6, 12)),

  // ── bone landmarks ────────────────────────────────────────────────────────
  ...pairPainRegions("bone-calcaneus", ellipsePath(84, 495, 9, 8)),

  // ── nerve track ──────────────────────────────────────────────────────────
  // Sciatic: from beside the sacrum, through the buttock, down the back of the
  // thigh to the popliteal fossa.
  ...pairPainRegions(
    "nerve-sciatic",
    "M 98 250 C 95 266 91 286 87 308 C 84 330 82 348 82 362 C 84 367 89 367 90 360 C 92 344 95 324 98 302 C 101 282 104 264 105 252 C 105 247 100 246 98 250 Z",
  ),

  // ── dermatome bands (educational overlay only) ───────────────────────────
  ...pairPainRegions(
    "derm-l4",
    "M 101 300 C 99 320 97 342 97 362 C 99 368 103 368 104 362 C 105 342 107 320 109 302 C 109 297 103 296 101 300 Z",
  ),
  ...pairPainRegions(
    "derm-l5",
    "M 92 310 C 90 332 89 356 89 380 C 89 402 91 420 94 434 C 97 438 101 438 101 428 C 100 408 98 386 98 364 C 99 344 101 324 102 312 C 101 307 94 306 92 310 Z",
  ),
  ...pairPainRegions(
    "derm-s1",
    "M 84 372 C 82 392 81 414 82 436 C 83 454 86 468 90 480 C 93 483 97 482 96 472 C 92 456 90 438 90 418 C 90 398 91 382 93 374 C 92 369 86 368 84 372 Z",
  ),
];
